<template>
  <div class="player-dashboard basketball-player-dashboard">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    
    <!-- Main Content -->
    <main class="dashboard-main">
      <div class="dashboard-container">
        <!-- Header -->
        <div class="dashboard-header">
          <div class="header-content">
            <div class="player-info">
              <div class="player-avatar">
                <i class="fa-solid fa-user"></i>
              </div>
              <div class="player-details">
                <h1>{{ playerInfo.name || 'Player Dashboard' }}</h1>
                <p class="player-email">{{ playerInfo.email }}</p>
                <div v-if="playerInfo.phone" class="player-phone">
                  <i class="fa-solid fa-phone"></i>
                  {{ playerInfo.phone }}
                </div>
              </div>
            </div>
            <button class="btn-refresh" @click="loadDashboardData" :disabled="loading">
              <i class="fa-solid fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              Refresh
            </button>
          </div>
        </div>

        <!-- Pending invites / memberships: no matches or tactics until active -->
        <div
          v-if="pendingInvites.length > 0 || pendingMemberships.length > 0"
          class="pending-access-section"
        >
          <h2 class="pending-section-title">
            <i class="fa-solid fa-envelope-open-text"></i>
            Team invitations & pending membership
          </h2>
          <p class="pending-section-hint">
            You can only see match and tactics data after you are an <strong>active</strong> member of a team.
            Accept an invite below, or wait until your membership is activated.
          </p>
          <div class="pending-cards">
            <div
              v-for="inv in pendingInvites"
              :key="'invite-' + inv.invite_id"
              class="pending-card invite-pending-card"
            >
              <div class="pending-card-body">
                <div class="pending-card-icon">
                  <i class="fa-solid fa-inbox"></i>
                </div>
                <div class="pending-card-text">
                  <h3>Invitation pending</h3>
                  <p>
                    <strong>{{ inv.team_name || 'A team' }}</strong>
                    <span v-if="inv.sender_name"> — from {{ inv.sender_name }}</span>
                  </p>
                  <p v-if="inv.expires_at" class="pending-meta">
                    <i class="fa-solid fa-clock"></i>
                    Expires {{ formatDate(inv.expires_at) }}
                  </p>
                </div>
              </div>
              <div class="pending-card-actions">
                <button
                  type="button"
                  class="btn-pending btn-accept"
                  :disabled="processingInviteId != null"
                  @click="acceptTeamInvite(inv.invite_id)"
                >
                  <i v-if="processingInviteId === inv.invite_id" class="fa-solid fa-spinner fa-spin"></i>
                  <i v-else class="fa-solid fa-check"></i>
                  Accept
                </button>
                <button
                  type="button"
                  class="btn-pending btn-decline"
                  :disabled="processingInviteId != null"
                  @click="declineTeamInvite(inv.invite_id)"
                >
                  <i v-if="processingInviteId === -inv.invite_id" class="fa-solid fa-spinner fa-spin"></i>
                  <i v-else class="fa-solid fa-times"></i>
                  Decline
                </button>
              </div>
            </div>

            <div
              v-for="m in pendingMemberships"
              :key="'mem-' + m.team_id + '-' + (m.member_status || '')"
              class="pending-card membership-pending-card"
            >
              <div class="pending-card-body">
                <div class="pending-card-icon muted">
                  <i class="fa-solid fa-hourglass-half"></i>
                </div>
                <div class="pending-card-text">
                  <h3>Membership not active</h3>
                  <p>
                    Your place on <strong>{{ m.name }}</strong> is still
                    <span class="status-pill">{{ m.member_status || 'pending' }}</span>.
                    You cannot access this team’s matches or tactics until your status is <strong>active</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content Grid -->
        <div class="content-grid">
          <!-- Left Column - Upcoming Matches -->
          <div class="column">
            <div class="card">
              <div class="card-header">
                <div class="card-title">
                  <i class="fa-solid fa-calendar"></i>
                  <h2>Upcoming Matches</h2>
                </div>
                <span class="matches-count">{{ upcomingMatches.length }}</span>
              </div>
              <div class="card-body">
                <div v-if="upcomingMatches.length > 0">
                  <div 
                    v-for="match in upcomingMatches" 
                    :key="match.match_id"
                    class="match-item booking-card"
                    :class="match.status"
                  >
                    <div class="booking-header">
                      <div class="booking-id">
                        <i class="fa-solid fa-futbol"></i>
                        <span>Match #{{ match.match_id }}</span>
                      </div>
                      <div class="booking-meta">
                        <span class="booking-date">
                          <i class="fa-solid fa-clock"></i>
                          {{ formatDate(match.match_date) }}
                        </span>
                        <span class="booking-status" :class="match.status">
                          {{ formatMatchStatus(match.status) }}
                        </span>
                      </div>
                    </div>
                    <div class="booking-body">
                      <div class="match-teams">
                        <div class="team-info" :class="{ 'home-team': match.is_home }">
                          <div class="team-name">{{ match.team_name }}</div>
                          <div class="team-badge" v-if="match.is_home">Home</div>
                        </div>
                        <div class="vs-divider">vs</div>
                        <div class="team-info" :class="{ 'away-team': !match.is_home }">
                          <div class="team-name">{{ match.opponent_name }}</div>
                          <div class="team-badge" v-if="!match.is_home">Away</div>
                        </div>
                      </div>
                      <div class="booking-details">
                        <div class="time-details">
                          <div class="time-slot">
                            <i class="fa-solid fa-calendar-day"></i>
                            {{ formatTime(match.match_date) }}
                          </div>
                          <div class="duration">
                            <i class="fa-solid fa-hourglass-half"></i>
                            {{ formatDuration(match.duration) }}
                          </div>
                        </div>
                      </div>
                      <div v-if="match.venue" class="booking-notes">
                        <i class="fa-solid fa-map-marker-alt"></i>
                        <div class="venue-info">
                          <span class="venue-text">{{ match.venue.name }}{{ match.venue.city ? ', ' + match.venue.city : '' }}</span>
                          <a 
                            v-if="match.venue.notes" 
                            :href="match.venue.notes" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="venue-location-link"
                            @click.stop
                          >
                            <i class="fa-solid fa-map-location-dot"></i>
                            View Location
                          </a>
                        </div>
                      </div>
                    </div>
                    <div class="booking-actions">
                      <button class="btn-action btn-secondary" @click.stop="viewMatchDetails(match)">
                        <i class="fa-solid fa-eye"></i>
                        Details
                      </button>
                      <button 
                        v-if="canViewMatchTactics(match)"
                        class="btn-action btn-tactics" 
                        @click.stop="toggleTacticsView(match.match_id)"
                        :disabled="loadingTactics[match.match_id]"
                      >
                        <i class="fa-solid fa-chess-board"></i>
                        <span v-if="loadingTactics[match.match_id]">Loading...</span>
                        <span v-else>{{ expandedTactics[match.match_id] ? 'Hide' : 'View' }} Tactics</span>
                      </button>
                    </div>
                    
                    <!-- Tactics View -->
                    <div v-if="expandedTactics[match.match_id]" class="tactics-view">
                      <div v-if="matchTactics[match.match_id] && matchTactics[match.match_id].player_positions" class="tactics-content">
                        <div class="tactics-header-info">
                          <div class="formation-info">
                            <span class="formation-label">Formation:</span>
                            <span class="formation-name">{{ matchTactics[match.match_id].formation_name || 'N/A' }}</span>
                            <span v-if="matchTactics[match.match_id].formation_category" class="formation-category">
                              ({{ capitalizeText(matchTactics[match.match_id].formation_category) }})
                            </span>
                          </div>
                        </div>
                        <div class="pitch-container-view">
                          <div class="pitch-wrapper-view">
                            <div class="basketball-court-view">
                              <img
                                src="@/assets/images/football_pitch/basketballcourt.png"
                                alt="Basketball court"
                                class="pitch-image-view"
                                loading="lazy"
                                decoding="async"
                              />
                              
                              <!-- Player Positions -->
                              <div
                                v-for="(position, index) in matchTactics[match.match_id].player_positions"
                                :key="index"
                                class="player-position-view"
                                :class="{ 'center-position': position.role === 'center' }"
                                :style="getTacticsPositionStyle(position)"
                              >
                                <div v-if="position.player_id" class="player-marker-view" :class="position.role">
                                  <div class="player-number-view">{{ position.player_number || '?' }}</div>
                                  <div class="player-name-tag-view">{{ position.player_name || 'Player' }}</div>
                                </div>
                                <div v-else class="empty-position-view">
                                  <i class="fa-solid fa-plus"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="!loadingTactics[match.match_id]" class="tactics-empty">
                        <i class="fa-solid fa-info-circle"></i>
                        <p>No tactics have been set for this match yet.</p>
                      </div>
                      <div v-else class="tactics-loading">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        <p>Loading tactics...</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">
                  <div class="empty-icon">
                    <i class="fa-solid fa-calendar-times"></i>
                  </div>
                  <template v-if="pendingInvites.length > 0 || pendingMemberships.length > 0">
                    <h3>No upcoming matches yet</h3>
                    <p>Accept a team invitation to become an active member and see matches and tactics here.</p>
                  </template>
                  <template v-else>
                    <h3>No Upcoming Matches</h3>
                    <p>You don't have any upcoming matches scheduled</p>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column - Teams & Info -->
          <div class="column">
            <!-- Teams Section -->
            <div class="card">
              <div class="card-header">
                <div class="card-title">
                  <i class="fa-solid fa-users"></i>
                  <h2>My Teams</h2>
                </div>
              </div>
              <div class="card-body">
                <div v-if="teams.length > 0">
                  <div 
                    v-for="team in teams" 
                    :key="team.team_id"
                    class="team-item"
                  >
                    <div class="team-logo">
                      <img
                        v-if="team.logo_url"
                        :src="team.logo_url"
                        :alt="team.name"
                        loading="lazy"
                        decoding="async"
                        @error="handleImageError"
                      />
                      <i v-else class="fa-solid fa-users"></i>
                    </div>
                    <div class="team-info-section">
                      <div class="team-name">{{ team.name }}</div>
                      <div class="team-meta">
                        <span class="team-position" v-if="team.position">
                          <i class="fa-solid fa-user-tag"></i>
                          {{ team.position }}
                        </span>
                        <span class="team-role" :class="team.member_role">
                          <i class="fa-solid fa-star" v-if="team.is_captain"></i>
                          {{ capitalizeText(team.member_role) }}
                        </span>
                      </div>
                      <div class="team-location">
                        <i class="fa-solid fa-map-marker-alt"></i>
                        {{ team.county }}{{ team.constituency ? ', ' + team.constituency : '' }}
                      </div>
                    </div>
                    <button class="btn-view-team" @click="viewTeamDashboard()">
                      <i class="fa-solid fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
                <div v-else class="empty-state">
                  <div class="empty-icon">
                    <i class="fa-solid fa-users-slash"></i>
                  </div>
                  <template v-if="pendingInvites.length > 0 || pendingMemberships.length > 0">
                    <h3>No active teams yet</h3>
                    <p>Accept a team invitation above (or wait until your membership is active) to see your teams here.</p>
                  </template>
                  <template v-else>
                    <h3>No Teams</h3>
                    <p>You're not a member of any teams yet</p>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "BasketballPlayerDashboard",
  mixins: [toastMixin, globalLoadingMixin],
  components: {
    MainNavbar,
    ToastContainer
  },
  data() {
    return {
      loading: false,
      loadingMessage: 'Loading player dashboard...',
      playerInfo: {
        customer_number: null,
        name: '',
        email: '',
        phone: ''
      },
      teams: [],
      pendingInvites: [],
      pendingMemberships: [],
      processingInviteId: null,
      upcomingMatches: [],
      expandedTactics: {},
      matchTactics: {},
      loadingTactics: {}
    };
  },
  async mounted() {
    await this.loadDashboardData();
  },
  methods: {
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },
    
    async loadDashboardData(options = {}) {
      const { silentToast = false } = options;
      this.loading = true;
      this.loadingMessage = 'Loading player dashboard...';
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          this.showToast(
            'error',
            'Authentication Required',
            'Please log in to view your player dashboard.'
          );
          setTimeout(() => {
            this.$router.push('/ipromiseafrica/sign-in');
          }, 2000);
          return;
        }
        
        const response = await fetch(buildApiUrl("/api/basketball/players/dashboard"), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast(
              'error',
              'Session Expired',
              'Please log in again to continue.'
            );
            setTimeout(() => {
              this.$router.push('/ipromiseafrica/sign-in');
            }, 2000);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to load dashboard: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Set player info
        this.playerInfo = {
          customer_number: data.player?.customer_number || null,
          name: data.player?.name || 'Player',
          email: data.player?.email || '',
          phone: data.player?.phone || ''
        };
        
        // Set teams (active members only — from API)
        this.teams = data.teams || [];
        this.pendingInvites = data.pending_invites || [];
        this.pendingMemberships = data.pending_memberships || [];

        // Set upcoming matches (active members only — from API)
        this.upcomingMatches = data.upcoming_matches || [];
        
        // Initialize tactics data structures
        this.upcomingMatches.forEach(match => {
          this.expandedTactics[match.match_id] = false;
          this.matchTactics[match.match_id] = null;
          this.loadingTactics[match.match_id] = false;
        });
        
        if (!silentToast) {
          const welcome =
            this.teams.length === 0 &&
            (this.pendingInvites.length > 0 || this.pendingMemberships.length > 0)
              ? 'You have pending invitations or membership — accept an invite to access team matches.'
              : `Welcome back, ${this.playerInfo.name}!`;
          this.showToast('success', 'Dashboard Loaded', welcome);
        }
        
      } catch (error) {
        console.error('Error loading player dashboard:', error);
        this.showToast(
          'error',
          'Failed to Load Dashboard',
          error.message || 'Please try again later.'
        );
      } finally {
        this.loading = false;
      }
    },
    
    viewMatchDetails(match) {
      if (!match?.match_id || match.team_id == null) return;
      this.$router
        .push({
          name: "BasketballTacticsAndFormations",
          params: {
            team_id: String(match.team_id),
            match_id: String(match.match_id),
          },
        })
        .catch(() => {});
    },

    canViewMatchTactics(match) {
      const s = (match?.status || '').toLowerCase();
      return ['confirmed', 'scheduled'].includes(s);
    },
    
    viewTeamDashboard() {
      this.$router.push({ name: "BasketballTeamDashboard" }).catch(() => {});
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatDuration(minutes) {
      if (!minutes) return 'N/A';
      const mins = parseInt(minutes);
      return `${mins} minute${mins !== 1 ? 's' : ''}`;
    },
    
    formatMatchStatus(status) {
      if (!status) return 'Unknown';
      const statusMap = {
        'pending': 'Pending',
        'confirmed': 'Confirmed',
        'scheduled': 'Scheduled',
        'past': 'Past',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };
      return statusMap[status.toLowerCase()] || this.capitalizeText(status);
    },
    
    capitalizeText(text) {
      if (!text) return '';
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },
    
    handleImageError(event) {
      event.target.style.display = 'none';
      const icon = document.createElement('i');
      icon.className = 'fa-solid fa-users';
      event.target.parentElement.appendChild(icon);
    },
    

    async acceptTeamInvite(inviteId) {
      if (this.processingInviteId != null || !inviteId) return;
      const token = this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Sign in required', 'Please log in again.');
        return;
      }
      this.processingInviteId = inviteId;
      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/basketball/invites/${inviteId}/accept`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const errBody = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = errBody.detail;
          const msg =
            typeof detail === 'string'
              ? detail
              : `Could not accept invite (${response.status})`;
          throw new Error(msg);
        }
        this.showToast('success', 'Joined team', errBody.message || 'You are now an active member.');
        await this.loadDashboardData({ silentToast: true });
      } catch (e) {
        console.error(e);
        this.showToast('error', 'Invite', e.message || 'Failed to accept invite.');
      } finally {
        this.processingInviteId = null;
      }
    },

    async declineTeamInvite(inviteId) {
      if (this.processingInviteId != null || !inviteId) return;
      const token = this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Sign in required', 'Please log in again.');
        return;
      }
      this.processingInviteId = -inviteId;
      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/basketball/invites/${inviteId}/reject`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const errBody = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = errBody.detail;
          const msg =
            typeof detail === 'string'
              ? detail
              : `Could not decline invite (${response.status})`;
          throw new Error(msg);
        }
        this.showToast('success', 'Invite declined', errBody.message || 'Invitation declined.');
        await this.loadDashboardData({ silentToast: true });
      } catch (e) {
        console.error(e);
        this.showToast('error', 'Invite', e.message || 'Failed to decline invite.');
      } finally {
        this.processingInviteId = null;
      }
    },

    async toggleTacticsView(matchId) {
      // Toggle expanded state
      this.expandedTactics[matchId] = !this.expandedTactics[matchId];
      
      // If expanding and tactics not loaded, fetch them
      if (this.expandedTactics[matchId] && !this.matchTactics[matchId]) {
        await this.loadMatchTactics(matchId);
      }
    },
    
    async loadMatchTactics(matchId) {
      const match = this.upcomingMatches.find(m => m.match_id === matchId);
      if (!match || !match.team_id) return;
      
      this.loadingTactics[matchId] = true;
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('Authentication required');
        }
        
        const response = await fetch(
          buildApiUrl(`/api/basketball/tactics/matches/${matchId}/teams/${match.team_id}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          if (response.status === 404) {
            // No tactics set yet
            this.matchTactics[matchId] = null;
            return;
          }
          if (response.status === 403) {
            this.showToast(
              'error',
              'Tactics unavailable',
              'You must be an active team member to view tactics.'
            );
            this.expandedTactics[matchId] = false;
            return;
          }
          throw new Error(`Failed to load tactics: ${response.status}`);
        }
        
        const data = await response.json();
        const tactics = data.tactics;
        
        if (tactics && tactics.player_positions) {
          // Map player positions with player info
          // For now, we'll display player_id as the number since we don't have direct access to player names
          // The player_id is the team_member_number from the database
          const tacticsWithPlayerInfo = {
            formation_name: tactics.formation_name,
            formation_category: tactics.formation_category,
            player_positions: tactics.player_positions.map((pos) => {
              if (pos.player_id) {
                // Use last 2 digits of player_id as a simple number display
                // In production, you'd want to fetch actual player names
                const displayNumber = pos.player_id % 100;
                return {
                  ...pos,
                  player_id: pos.player_id,
                  player_number: displayNumber,
                  player_name: `#${displayNumber}`
                };
              }
              return pos;
            })
          };
          
          this.matchTactics[matchId] = tacticsWithPlayerInfo;
        } else {
          this.matchTactics[matchId] = null;
        }
        
      } catch (error) {
        console.error('Error loading match tactics:', error);
        this.showToast(
          'error',
          'Failed to Load Tactics',
          error.message || 'Could not load tactics for this match.'
        );
        this.matchTactics[matchId] = null;
      } finally {
        this.loadingTactics[matchId] = false;
      }
    },
    
    getTacticsPositionStyle(position) {
      return {
        left: `${position.position_x}%`,
        top: `${position.position_y}%`,
        transform: 'translate(-50%, -50%)'
      };
    }
  }
};
</script>

<style scoped>
.player-dashboard {
  background: var(--dashboard-bg);
  min-height: 100vh;
  padding-top: 120px;
}

.dashboard-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.pending-access-section {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 24px 28px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.pending-section-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pending-section-hint {
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  line-height: 1.5;
}

.pending-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pending-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.pending-card-body {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex: 1;
  min-width: 200px;
}

.pending-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.2);
  color: #93c5fd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.pending-card-icon.muted {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
}

.pending-card-text h3 {
  margin: 0 0 6px;
  font-size: 1.05rem;
}

.pending-card-text p {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.45;
}

.pending-meta {
  margin-top: 8px !important;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.55) !important;
}

.pending-card-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.btn-pending {
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;
}

.btn-pending:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-pending.btn-accept {
  background: linear-gradient(135deg, #059669, #10b981);
  color: #fff;
}

.btn-pending.btn-decline {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(234, 179, 8, 0.2);
  color: #fde047;
  text-transform: lowercase;
  font-size: 0.9em;
}

/* Header */
.dashboard-header {
  background: var(--card-bg);
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.player-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.player-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--action-primary-bg), var(--action-primary-color));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  border: 4px solid var(--action-primary-color);
}

.player-details h1 {
  margin: 0 0 8px 0;
  font-size: 1.8rem;
  color: var(--heading-color);
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.player-email {
  color: var(--subtext-color);
  margin: 0 0 8px 0;
  font-size: 1rem;
}

.player-phone {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.btn-refresh {
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--text-color1);
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* Card Styles */
.card {
  background: var(--card-bg);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.card-header {
  padding: 24px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-title i {
  color: var(--section-icon);
  font-size: 1.3rem;
}

.card-title h2 {
  margin: 0;
  font-size: 1.3rem;
  color: var(--heading-color);
}

.matches-count {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
}

.card-body {
  padding: 24px 30px;
}

/* Match Item */
.match-item {
  margin-bottom: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.match-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateX(4px);
}

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.booking-id {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--heading-color);
  font-weight: 600;
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.booking-date {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.booking-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.booking-status.pending {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.booking-status.confirmed {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.booking-status.past {
  background: rgba(148, 163, 184, 0.25);
  color: #cbd5e1;
}

.booking-status.scheduled {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.match-teams {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
}

.team-info {
  flex: 1;
  text-align: center;
}

.team-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--heading-color);
  margin-bottom: 8px;
}

.team-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.vs-divider {
  font-weight: 700;
  color: var(--subtext-color);
  font-size: 1.2rem;
}

.booking-details {
  margin-bottom: 12px;
}

.time-details {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}

.time-slot,
.duration {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.booking-notes {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--subtext-color);
  font-size: 0.9rem;
  margin-top: 12px;
}

.venue-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.venue-text {
  color: var(--text-color1);
}

.venue-location-link {
  color: var(--action-primary-color);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.venue-location-link:hover {
  color: var(--action-primary-bg);
  text-decoration: underline;
}

.booking-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.btn-action {
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-color1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.btn-tactics {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.4);
}

.btn-tactics:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.3);
  transform: translateY(-2px);
}

.btn-tactics:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Tactics View */
.tactics-view {
  margin-top: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tactics-header-info {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.formation-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.formation-label {
  color: var(--subtext-color);
  font-weight: 600;
}

.formation-name {
  color: var(--heading-color);
  font-weight: 700;
  font-size: 1.1rem;
}

.formation-category {
  color: var(--subtext-color);
  font-size: 0.9rem;
}

.pitch-container-view {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  padding-bottom: 60%;
}

.pitch-wrapper-view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.soccer-pitch-view {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.pitch-image-view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  user-select: none;
  pointer-events: none;
}

.player-position-view {
  position: absolute;
  width: 50px;
  height: 50px;
  z-index: 10;
}

@media (max-width: 768px) {
  .player-position-view {
    width: 40px;
    height: 40px;
  }
}

.player-marker-view {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 3px solid;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  position: relative;
}

.player-marker-view.forward {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  border-color: #ff4757;
}

.player-marker-view.midfielder {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
  border-color: #00d2d3;
}

.player-marker-view.defender {
  background: linear-gradient(135deg, #95a5a6, #7f8c8d);
  border-color: #636e72;
}

.player-marker-view.goalkeeper {
  background: linear-gradient(135deg, #feca57, #ff9ff3);
  border-color: #ff6348;
  border-width: 4px;
  z-index: 15;
}

.player-number-view {
  font-weight: 700;
  font-size: 0.9rem;
  color: white;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.player-name-tag-view {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  white-space: nowrap;
  font-weight: 600;
  pointer-events: none;
}

.empty-position-view {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
}

.tactics-empty,
.tactics-loading {
  text-align: center;
  padding: 40px 20px;
  color: var(--subtext-color);
}

.tactics-empty i,
.tactics-loading i {
  font-size: 2rem;
  margin-bottom: 12px;
  opacity: 0.6;
}

.tactics-loading i {
  color: var(--action-primary-color);
}

.tactics-empty p,
.tactics-loading p {
  margin: 0;
  font-size: 0.95rem;
}

/* Team Item */
.team-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.team-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateX(4px);
}

.team-logo {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.team-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.team-logo i {
  font-size: 1.5rem;
  color: var(--subtext-color);
}

.team-info-section {
  flex: 1;
  min-width: 0;
}

.team-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--heading-color);
  margin-bottom: 8px;
}

.team-meta {
  display: flex;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.team-position,
.team-role {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.team-role.captain {
  color: #fbbf24;
  font-weight: 600;
}

.team-location {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.btn-view-team {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.btn-view-team:hover {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  transform: translateX(4px);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--subtext-color);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: var(--heading-color);
  font-size: 1.3rem;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

@media (max-width: 768px) {
  .dashboard-main {
    padding: 15px;
  }
  
  .dashboard-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .match-teams {
    flex-direction: column;
  }
}
</style>
